import { Box, Image, Button, EditIcon, Flex } from '@iheartjane/reefer';
import { useCallback } from 'react';
import type { DropzoneOptions, FileRejection } from 'react-dropzone';
import { useDropzone, ErrorCode } from 'react-dropzone';
import { Controller } from 'react-hook-form';

import { MAX_IMAGE_SIZE } from '@jane/business-admin/util';
import { FileUploadArea } from '@jane/shared-b2b/components';
import { ImagePreview } from '@jane/shared/util';

import styles from './ImageUploader.module.css';

const IMAGE_TOO_LARGE_ERROR =
  'This logo is over the 2MB limit. Resize it and try again.';
const IMAGE_WRONG_FORMAT_ERROR =
  'This file type is not supported. Try using .jpg or .png files.';

// Narrower than the shared ACCEPTED_IMAGE_FILE_TYPES, which allows any `image/*`.
const ACCEPTED_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

const ERROR_MESSAGES: Record<string, string> = {
  [ErrorCode.FileInvalidType]: IMAGE_WRONG_FORMAT_ERROR,
  [ErrorCode.FileTooLarge]: IMAGE_TOO_LARGE_ERROR,
};

export const ImageUploader = ({
  height,
  imageUrl,
  isLoading,
  onError,
  onUpdateImage,
  width,
}: {
  height: number | string;
  imageUrl?: string;
  isLoading: boolean;
  onError: (errors: string[]) => void;
  onUpdateImage: (imageUrl: string) => void;
  width: number | string;
}) => {
  const onDrop = useCallback(
    async ([file]: File[]) => {
      if (!file) {
        return;
      }

      const image = await ImagePreview.parse(file);
      onUpdateImage(image.url);
      onError([]);
    },
    [onUpdateImage, onError]
  );

  const dropzoneOptions: DropzoneOptions = {
    accept: ACCEPTED_IMAGE_TYPES,
    maxSize: MAX_IMAGE_SIZE,
    multiple: false,
    noClick: true,
    onDrop,
    onDropRejected: ([{ errors }]: FileRejection[]) =>
      onError(
        errors
          .map(({ code }) => ERROR_MESSAGES[code])
          .filter((message) => message !== undefined)
      ),
  };

  const { getRootProps, getInputProps, open } = useDropzone(dropzoneOptions);

  return (
    <Box width={width} height={height}>
      {imageUrl ? (
        <div {...getRootProps()}>
          <Flex
            justifyContent="center"
            className={styles['imageContainer']}
            borderRadius="sm"
            border="grays-light"
            p={16}
            position="relative"
            height={height}
            width={width}
          >
            <Image
              key={imageUrl}
              src={imageUrl}
              height="100%"
              width="100%"
              altText="Menu Logo"
              borderRadius="16px"
              objectFit="contain"
            />
            <Box position="absolute" top={16} right={16}>
              <Button.Icon
                label="Edit"
                variant="secondary"
                icon={<EditIcon />}
                onClick={open}
              />
              {/* Hidden input for form value (image URL string) */}
              <Controller
                name="logo"
                render={({ field }) => <input type="hidden" {...field} />}
              />
              {/* File input for dropzone */}
              <input {...getInputProps()} />
            </Box>
          </Flex>
        </div>
      ) : (
        <FileUploadArea
          {...dropzoneOptions}
          isLoading={isLoading && !imageUrl}
          label="Upload image"
          dragLabel="Drag and drop image here"
        />
      )}
    </Box>
  );
};
